import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";

const CATEGORY_LABELS = {
    leash_rules: "Leash rules",
    fencing: "Fencing",
    amenities: "Amenities",
    difficulty: "Difficulty",
    surface: "Surface",
    activity_style: "Activity style",
    vibe: "Vibe",
};

const SpotDetailDrawer = ({ spotId, open, onClose, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [spot, setSpot] = useState(null);
    const [tags, setTags] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [reviewPhotos, setReviewPhotos] = useState([]);
    const [allTags, setAllTags] = useState([]);

    // Thumbnail management
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0); // bump to refetch detail
    const fileInputRef = useRef(null);
    const editedSpotIdRef = useRef(null);

    // Editable fields
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [type, setType] = useState("park");
    const [selectedTagIds, setSelectedTagIds] = useState(new Set());
    const [hideIds, setHideIds] = useState(new Set());
    const [deleteIds, setDeleteIds] = useState(new Set());

    useEffect(() => {
        if (!open || !spotId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [detailRes, tagsRes] = await Promise.all([
                    AxiosInstance.get(`${ApiList.SPOT_ADMIN_DETAIL}/${spotId}`),
                    AxiosInstance.get(ApiList.SPOT_ADMIN_TAGS),
                ]);
                if (cancelled) return;
                if (detailRes?.data?.s === 1) {
                    const r = detailRes.data.r;
                    setSpot(r.spot);
                    setTags(r.tags || []);
                    setPhotos(r.photos || []);
                    setReviewPhotos(r.review_photos || []);
                    // Photo actions refetch via refreshKey — don't clobber
                    // in-progress name/description/tag edits on those reloads.
                    if (editedSpotIdRef.current !== r.spot?.id) {
                        editedSpotIdRef.current = r.spot?.id;
                        setName(r.spot?.name || "");
                        setDescription(r.spot?.description || "");
                        setType(r.spot?.type || "park");
                        setSelectedTagIds(new Set((r.tags || []).map((t) => t.id)));
                        setHideIds(new Set());
                        setDeleteIds(new Set());
                    }
                }
                if (tagsRes?.data?.s === 1) {
                    setAllTags((tagsRes.data.r.tags || []).filter((t) => t.status === "active"));
                }
            } catch (err) {
                console.error(err);
                toast.error("Failed to load spot");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, spotId, refreshKey]);

    const tagsByCategory = useMemo(() => {
        const grouped = {};
        for (const t of allTags) {
            if (t.applies_to !== "both" && t.applies_to !== type) continue;
            if (!grouped[t.category]) grouped[t.category] = [];
            grouped[t.category].push(t);
        }
        return grouped;
    }, [allTags, type]);

    const toggleTag = (tagId, isMutex, category) => {
        setSelectedTagIds((prev) => {
            const next = new Set(prev);
            if (next.has(tagId)) {
                next.delete(tagId);
            } else {
                // Mutex: clear other mutex tags in this category
                if (isMutex) {
                    for (const t of allTags) {
                        if (t.category === category && t.is_mutually_exclusive) next.delete(t.id);
                    }
                }
                next.add(tagId);
            }
            return next;
        });
    };

    const togglePhotoHide = (id) => {
        setHideIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else {
                next.add(id);
                const nd = new Set(deleteIds);
                nd.delete(id);
                setDeleteIds(nd);
            }
            return next;
        });
    };
    const togglePhotoDelete = (id) => {
        setDeleteIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else {
                next.add(id);
                const nh = new Set(hideIds);
                nh.delete(id);
                setHideIds(nh);
            }
            return next;
        });
    };

    // Re-arm the "reset editable fields" guard when the drawer closes so
    // reopening the same spot starts from fresh server state.
    useEffect(() => {
        if (!open) editedSpotIdRef.current = null;
    }, [open]);

    // ------------------------------------------------------------------
    // Thumbnail management. All three actions save instantly server-side
    // (unlike hide/delete which batch into "Save edits"), then refetch.
    // ------------------------------------------------------------------
    const uploadPhoto = async (file) => {
        if (!spot || uploading || !file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Only image files are allowed");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("photo", file);
            fd.append("make_primary", 1); // dropped photo instantly becomes the thumbnail
            const res = await AxiosInstance.post(`${ApiList.SPOT_ADMIN_PHOTO}/${spot.id}/photo`, fd);
            if (res?.data?.s === 1) {
                toast.success("Photo uploaded — set as thumbnail");
                setRefreshKey((k) => k + 1);
                if (onChanged) onChanged();
            } else {
                toast.error(res?.data?.m || "Upload failed");
            }
        } catch {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const setPrimaryPhoto = async (photoId) => {
        if (!spot) return;
        try {
            const res = await AxiosInstance.post(
                `${ApiList.SPOT_ADMIN_PHOTO}/${spot.id}/photo/${photoId}/primary`
            );
            if (res?.data?.s === 1) {
                toast.success("Thumbnail updated");
                setRefreshKey((k) => k + 1);
                if (onChanged) onChanged();
            } else {
                toast.error(res?.data?.m || "Failed to set thumbnail");
            }
        } catch {
            toast.error("Failed to set thumbnail");
        }
    };

    const promoteReviewPhoto = async (reviewPhotoId) => {
        if (!spot) return;
        try {
            const res = await AxiosInstance.post(
                `${ApiList.SPOT_ADMIN_PHOTO}/${spot.id}/promote-review-photo/${reviewPhotoId}`
            );
            if (res?.data?.s === 1) {
                toast.success("Review photo promoted to thumbnail");
                setRefreshKey((k) => k + 1);
                if (onChanged) onChanged();
            } else {
                toast.error(res?.data?.m || "Promote failed");
            }
        } catch {
            toast.error("Promote failed");
        }
    };

    const copyCoords = () => {
        if (!spot) return;
        const s = `${spot.latitude}, ${spot.longitude}`;
        navigator.clipboard.writeText(s);
        toast.info("Coordinates copied");
    };

    const saveChanges = async () => {
        if (!spot || saving) return;
        setSaving(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || null,
                type,
                tag_ids: Array.from(selectedTagIds).join(","),
            };
            if (hideIds.size > 0) body.photo_ids_to_hide = Array.from(hideIds);
            if (deleteIds.size > 0) body.photo_ids_to_delete = Array.from(deleteIds);

            const res = await AxiosInstance.patch(`${ApiList.SPOT_ADMIN_EDIT}/${spot.id}`, body);
            if (res?.data?.s === 1) {
                toast.success("Spot updated");
                if (onChanged) onChanged();
            } else {
                toast.error(res?.data?.m || "Save failed");
            }
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const approveFromDrawer = async () => {
        if (!spot) return;
        const result = await Swal.fire({
            title: "Approve this spot?",
            text: "The submitter gets a push notification. Unsaved edits here are NOT saved by approve — save first if needed.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2e7d32",
            confirmButtonText: "Approve",
        });
        if (!result.isConfirmed) return;
        try {
            const res = await AxiosInstance.post(`${ApiList.SPOT_ADMIN_APPROVE}/${spot.id}/approve`);
            if (res?.data?.s === 1) {
                toast.success("Spot approved");
                if (onChanged) onChanged();
            } else {
                toast.error(res?.data?.m || "Approve failed");
            }
        } catch {
            toast.error("Approve failed");
        }
    };

    const rejectFromDrawer = async () => {
        if (!spot) return;
        const { value: reason } = await Swal.fire({
            title: "Reject this spot?",
            input: "textarea",
            inputLabel: "Reason (admin only)",
            inputAttributes: { maxlength: 255, rows: 3 },
            inputValidator: (v) => (!v || v.trim().length < 2 ? "Please provide a reason" : null),
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Reject",
        });
        if (!reason) return;
        try {
            const res = await AxiosInstance.post(
                `${ApiList.SPOT_ADMIN_REJECT}/${spot.id}/reject`,
                { reason: reason.trim() }
            );
            if (res?.data?.s === 1) {
                toast.success("Spot rejected");
                if (onChanged) onChanged();
            } else {
                toast.error(res?.data?.m || "Reject failed");
            }
        } catch {
            toast.error("Reject failed");
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: "100%", md: 560 }, background: "#fff" } }}
        >
            <Box sx={{ p: 2, display: "flex", alignItems: "center", borderBottom: "1px solid #0001" }}>
                <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#000", flex: 1 }}>
                    Spot detail {spot ? `· #${spot.id}` : ""}
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {loading ? (
                <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                </Box>
            ) : !spot ? (
                <Box sx={{ p: 4, color: "#666" }}>No spot loaded.</Box>
            ) : (
                <Box sx={{ p: 2, pb: 14 }}>
                    {/* Status */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Chip
                            size="small"
                            label={spot.status.toUpperCase()}
                            sx={{
                                background:
                                    spot.status === "approved" ? "#2e7d3220"
                                    : spot.status === "rejected" ? "#d3331f"
                                    : spot.status === "archived" ? "#8884"
                                    : "#c6ff00",
                                color: "#000",
                                fontWeight: 700,
                            }}
                        />
                        {spot.status === "rejected" && spot.rejection_reason && (
                            <Typography sx={{ fontSize: 12, color: "#d33" }}>
                                Reason: {spot.rejection_reason}
                            </Typography>
                        )}
                    </Box>

                    {/* Name + description */}
                    <TextField
                        fullWidth
                        label="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ mb: 2 }}
                        inputProps={{ maxLength: 160 }}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                        inputProps={{ maxLength: 2000 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={type}
                            label="Type"
                            onChange={(e) => setType(e.target.value)}
                        >
                            <MenuItem value="park">Park</MenuItem>
                            <MenuItem value="trail">Trail</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Submitter + coords */}
                    <Box sx={{ background: "#b9cc0320", p: 1.5, borderRadius: 1, mb: 2 }}>
                        <Typography sx={{ fontSize: 12, color: "#000", fontWeight: 600 }}>
                            Submitter
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#000" }}>
                            {spot.submitter_username || spot.submitter_fname || "—"}
                            {spot.submitter_email ? ` · ${spot.submitter_email}` : ""}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <Typography sx={{ fontSize: 12, color: "#000", fontWeight: 600 }}>
                                Coords:
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#000" }}>
                                {spot.latitude}, {spot.longitude}
                            </Typography>
                            <Tooltip title="Copy">
                                <IconButton size="small" onClick={copyCoords}>
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Tag editor */}
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#000", mb: 1 }}>
                        Tags
                    </Typography>
                    {Object.keys(tagsByCategory).length === 0 && (
                        <Typography sx={{ fontSize: 12, color: "#666" }}>
                            No tags apply to {type}
                        </Typography>
                    )}
                    {Object.entries(tagsByCategory).map(([cat, catTags]) => (
                        <Box key={cat} sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                                {CATEGORY_LABELS[cat] || cat}
                                {catTags.some((t) => t.is_mutually_exclusive) && (
                                    <span style={{ fontSize: 10, marginLeft: 6, color: "#999" }}>(pick one)</span>
                                )}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {catTags.map((t) => {
                                    const selected = selectedTagIds.has(t.id);
                                    return (
                                        <Chip
                                            key={t.id}
                                            size="small"
                                            label={t.name}
                                            onClick={() => toggleTag(t.id, !!t.is_mutually_exclusive, t.category)}
                                            sx={{
                                                background: selected ? "#c6ff00" : "#fff",
                                                color: "#000",
                                                border: "1px solid",
                                                borderColor: selected ? "#c6ff00" : "#ccc",
                                                fontWeight: selected ? 700 : 400,
                                                cursor: "pointer",
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}

                    {/* Photos */}
                    <Divider sx={{ my: 2 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#000", mb: 1 }}>
                        Photos ({photos.length})
                    </Typography>
                    {/* Drag-and-drop / click upload zone — new photo becomes the thumbnail */}
                    <Box
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            uploadPhoto(e.dataTransfer.files?.[0]);
                        }}
                        sx={{
                            border: "2px dashed",
                            borderColor: dragOver ? "#c6ff00" : "#ccc",
                            background: dragOver ? "#c6ff0020" : "#fafafa",
                            borderRadius: 1,
                            p: 1.5,
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            cursor: "pointer",
                        }}
                    >
                        {uploading ? (
                            <CircularProgress size={18} />
                        ) : (
                            <AddPhotoAlternateIcon sx={{ fontSize: 18, color: "#666" }} />
                        )}
                        <Typography sx={{ fontSize: 12, color: "#666" }}>
                            {uploading ? "Uploading…" : "Drop an image here (or click) — becomes the thumbnail"}
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                                uploadPhoto(e.target.files?.[0]);
                                e.target.value = ""; // allow re-selecting the same file
                            }}
                        />
                    </Box>

                    {photos.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: "#666" }}>No photos</Typography>
                    ) : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {photos.map((ph, idx) => {
                                const willHide = hideIds.has(ph.id);
                                const willDelete = deleteIds.has(ph.id);
                                const dim = ph.status !== "active" || willHide || willDelete;
                                // Thumbnail = first active photo (list arrives ordered by sort_order, id)
                                const isPrimary =
                                    ph.status === "active" &&
                                    photos.findIndex((p) => p.status === "active") === idx;
                                return (
                                    <Box
                                        key={ph.id}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: 1,
                                            overflow: "hidden",
                                            position: "relative",
                                            border: willDelete ? "2px solid #d33" : willHide ? "2px solid #f90" : "1px solid #ddd",
                                            opacity: dim ? 0.55 : 1,
                                        }}
                                    >
                                        <img
                                            src={`${ApiList.IMG_URL}${ph.url}`}
                                            alt=""
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                        <Box sx={{ position: "absolute", bottom: 0, right: 0, display: "flex", background: "#fffc" }}>
                                            <Tooltip title={isPrimary ? "Current thumbnail" : "Set as thumbnail"}>
                                                <IconButton size="small" onClick={() => !isPrimary && setPrimaryPhoto(ph.id)}>
                                                    {isPrimary
                                                        ? <StarIcon sx={{ fontSize: 14, color: "#e6b800" }} />
                                                        : <StarBorderIcon sx={{ fontSize: 14, color: "#000" }} />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={willHide ? "Undo hide" : "Hide"}>
                                                <IconButton size="small" onClick={() => togglePhotoHide(ph.id)}>
                                                    <VisibilityOffIcon sx={{ fontSize: 14, color: willHide ? "#f90" : "#000" }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={willDelete ? "Undo delete" : "Delete"}>
                                                <IconButton size="small" onClick={() => togglePhotoDelete(ph.id)}>
                                                    <DeleteOutlineIcon sx={{ fontSize: 14, color: willDelete ? "#d33" : "#000" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Box sx={{ position: "absolute", top: 2, left: 2, fontSize: 9, background: "#000a", color: "#fff", px: 0.5, borderRadius: 0.5 }}>
                                            {ph.status}
                                        </Box>
                                        {isPrimary && (
                                            <Box sx={{ position: "absolute", top: 2, right: 2, fontSize: 9, background: "#e6b800", color: "#000", fontWeight: 700, px: 0.5, borderRadius: 0.5 }}>
                                                THUMBNAIL
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {/* Review photos — promotable to spot thumbnail */}
                    {reviewPhotos.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#000", mb: 1 }}>
                                Review photos ({reviewPhotos.length})
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {reviewPhotos.map((rp) => (
                                    <Box
                                        key={rp.id}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: 1,
                                            overflow: "hidden",
                                            position: "relative",
                                            border: "1px solid #ddd",
                                        }}
                                    >
                                        <img
                                            src={`${ApiList.IMG_URL}${rp.url}`}
                                            alt=""
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                        <Box sx={{ position: "absolute", bottom: 0, right: 0, background: "#fffc" }}>
                                            <Tooltip title="Promote to thumbnail">
                                                <IconButton size="small" onClick={() => promoteReviewPhoto(rp.id)}>
                                                    <StarBorderIcon sx={{ fontSize: 14, color: "#000" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Box sx={{ position: "absolute", top: 2, left: 2, fontSize: 9, background: "#000a", color: "#fff", px: 0.5, borderRadius: 0.5 }}>
                                            {rp.reviewer_username || rp.reviewer_fname || "reviewer"}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            )}

            {/* Footer action bar */}
            {spot && !loading && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        width: { xs: "100%", md: 560 },
                        background: "#fff",
                        borderTop: "1px solid #0001",
                        p: 1.5,
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                    }}
                >
                    {spot.status === "pending" && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={rejectFromDrawer}
                                sx={{ color: "#d33", borderColor: "#d33" }}
                            >
                                Reject
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={approveFromDrawer}
                                sx={{ background: "#c6ff00", color: "#000", fontWeight: 700, "&:hover": { background: "#b5ee00" } }}
                            >
                                Approve
                            </Button>
                        </>
                    )}
                    <Button
                        variant="contained"
                        onClick={saveChanges}
                        disabled={saving}
                        sx={{ background: "#000", color: "#fff", "&:hover": { background: "#222" } }}
                    >
                        {saving ? "Saving…" : "Save edits"}
                    </Button>
                </Box>
            )}
        </Drawer>
    );
};

export default SpotDetailDrawer;
